import { Controller } from "@hotwired/stimulus"
import flatpickr from "flatpickr"

export default class extends Controller {
  static targets = [
    "buttonText",
    "dropdown",
    "customOption",
    "presetOption",
    "dateDisplay",
    "dateRangeContainer",
    "startDateInput",
    "endDateInput",
    "applyButton",
    "resetButton",
    "last7DaysDisplay",
    "last7DaysFrom",
    "last7DaysTo",
    "last30DaysDisplay",
    "last30DaysFrom",
    "last30DaysTo"
  ]

  static values = {
    maxDate: String
  }

  connect() {
    this.currentPreset = "last_7_days"
    this.startDate = null
    this.endDate = null

    // Calculate and display preset date ranges
    this.calculatePresetDates()

    // Check URL parameters on page load
    this.checkUrlParameters()

    // Initialize Flatpickr for date inputs
    this.initializeDatePickers()
  }

  calculatePresetDates() {
    // Use the max date from the server (most recent report_date)
    // If not available, fall back to today
    // Parse date string as local time by adding T00:00:00 to avoid timezone issues
    const maxDate = this.hasMaxDateValue ? new Date(this.maxDateValue + 'T00:00:00') : new Date()

    // Calculate Last 7 Days
    const last7DaysEnd = maxDate
    const last7DaysStart = new Date(maxDate)
    last7DaysStart.setDate(last7DaysStart.getDate() - 6)

    if (this.hasLast7DaysFromTarget && this.hasLast7DaysToTarget) {
      this.last7DaysFromTarget.textContent = this.formatDate(last7DaysStart)
      this.last7DaysToTarget.textContent = this.formatDate(last7DaysEnd)
    }

    // Calculate Last 30 Days
    const last30DaysEnd = maxDate
    const last30DaysStart = new Date(maxDate)
    last30DaysStart.setDate(last30DaysStart.getDate() - 29)

    if (this.hasLast30DaysFromTarget && this.hasLast30DaysToTarget) {
      this.last30DaysFromTarget.textContent = this.formatDate(last30DaysStart)
      this.last30DaysToTarget.textContent = this.formatDate(last30DaysEnd)
    }

    // Show Last 7 Days display by default
    this.showLast7DaysDisplay()
  }

  disconnect() {
    if (this.startDatePicker) {
      this.startDatePicker.destroy()
    }
    if (this.endDatePicker) {
      this.endDatePicker.destroy()
    }
  }

  checkUrlParameters() {
    const urlParams = new URLSearchParams(window.location.search)
    const startDate = urlParams.get('start_date')
    const endDate = urlParams.get('end_date')
    const preset = urlParams.get('date_preset')

    if (startDate && endDate) {
      // Custom date range from URL
      this.startDate = startDate
      this.endDate = endDate
      this.currentPreset = "custom"
      this.updateButtonText("Custom")
      this.highlightPreset("custom")
      // Don't show any date display for custom - button text is enough
    } else if (preset) {
      // Preset from URL
      this.currentPreset = preset
      this.updateButtonTextForPreset(preset)
      this.highlightPreset(preset)

      // Show appropriate date display
      if (preset === "last_7_days") {
        this.showLast7DaysDisplay()
      } else if (preset === "last_30_days") {
        this.showLast30DaysDisplay()
      }
    } else {
      // Default: highlight Last 7 Days
      this.highlightPreset("last_7_days")
    }
  }

  initializeDatePickers() {
    // Use the max date from the server (most recent report_date)
    const maxDate = this.hasMaxDateValue ? this.maxDateValue : "today"

    // Start date picker
    if (this.hasStartDateInputTarget) {
      this.startDatePicker = flatpickr(this.startDateInputTarget, {
        dateFormat: "Y-m-d",
        maxDate: maxDate,
        onChange: (selectedDates, dateStr) => {
          this.startDate = dateStr
          // Update end date picker's minDate
          if (this.endDatePicker) {
            this.endDatePicker.set('minDate', dateStr)
          }
        }
      })
    }

    // End date picker
    if (this.hasEndDateInputTarget) {
      this.endDatePicker = flatpickr(this.endDateInputTarget, {
        dateFormat: "Y-m-d",
        maxDate: maxDate,
        onChange: (selectedDates, dateStr) => {
          this.endDate = dateStr
        }
      })
    }
  }

  selectPreset(event) {
    event.preventDefault()
    const preset = event.currentTarget.dataset.preset

    // Highlight the selected preset
    this.highlightPreset(preset)

    if (preset === "custom") {
      // Show custom date range picker
      this.showCustomDatePicker()
    } else {
      // Handle preset selection
      this.currentPreset = preset
      const presetName = event.currentTarget.dataset.presetName
      this.updateButtonText(presetName)

      // Show the appropriate date display
      if (preset === "last_7_days") {
        this.showLast7DaysDisplay()
      } else if (preset === "last_30_days") {
        this.showLast30DaysDisplay()
      }
    }
  }

  highlightPreset(preset) {
    // Remove highlight from all preset options
    this.presetOptionTargets.forEach(option => {
      option.classList.remove("bg-indigo-50", "text-indigo-600")
      option.classList.add("text-gray-700")
    })

    // Add highlight to the selected preset
    const selectedOption = this.presetOptionTargets.find(option => {
      return option.dataset.preset === preset
    })

    if (selectedOption) {
      selectedOption.classList.remove("text-gray-700")
      selectedOption.classList.add("bg-indigo-50", "text-indigo-600")
    }
  }

  applyPreset(event) {
    event.preventDefault()
    const preset = event.currentTarget.dataset.preset

    // Close dropdown
    this.closeDropdown()

    // Apply the filter
    this.applyPresetFilter(preset)
  }

  showCustomDatePicker() {
    this.currentPreset = "custom"

    // Hide all date displays
    this.hideAllDateDisplays()

    // Show date range container
    if (this.hasDateRangeContainerTarget) {
      this.dateRangeContainerTarget.classList.remove("hidden")
    }

    // Update button text
    this.updateButtonText("Custom")

    // Focus on start date input
    if (this.hasStartDateInputTarget) {
      this.startDateInputTarget.focus()
    }
  }

  showLast7DaysDisplay() {
    this.hideAllDateDisplays()
    if (this.hasLast7DaysDisplayTarget) {
      this.last7DaysDisplayTarget.classList.remove("hidden")
    }
  }

  showLast30DaysDisplay() {
    this.hideAllDateDisplays()
    if (this.hasLast30DaysDisplayTarget) {
      this.last30DaysDisplayTarget.classList.remove("hidden")
    }
  }

  hideAllDateDisplays() {
    if (this.hasLast7DaysDisplayTarget) {
      this.last7DaysDisplayTarget.classList.add("hidden")
    }
    if (this.hasLast30DaysDisplayTarget) {
      this.last30DaysDisplayTarget.classList.add("hidden")
    }
    if (this.hasDateRangeContainerTarget) {
      this.dateRangeContainerTarget.classList.add("hidden")
    }
  }

  applyCustomDateRange(event) {
    event.preventDefault()

    if (!this.startDate || !this.endDate) {
      alert("Please select both start and end dates")
      return
    }

    // Validate date range
    if (new Date(this.startDate) > new Date(this.endDate)) {
      alert("Start date must be before end date")
      return
    }

    // Close dropdown
    this.closeDropdown()

    // Apply filter
    this.applyCustomFilter()
  }

  resetFilter(event) {
    event.preventDefault()

    // Reset to default (Last 7 Days)
    this.currentPreset = "last_7_days"
    this.startDate = null
    this.endDate = null

    // Clear date inputs
    if (this.startDatePicker) {
      this.startDatePicker.clear()
    }
    if (this.endDatePicker) {
      this.endDatePicker.clear()
    }

    // Show Last 7 Days display
    this.showLast7DaysDisplay()

    // Update button text
    this.updateButtonText("Last 7 Days")

    // Close dropdown and apply default filter
    this.closeDropdown()
    this.applyPresetFilter("last_7_days")
  }

  applyPresetFilter(preset) {
    const urlParams = new URLSearchParams(window.location.search)

    // Remove custom date params
    urlParams.delete('start_date')
    urlParams.delete('end_date')

    // Set preset parameter
    urlParams.set('date_preset', preset)

    // Navigate with Turbo
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`
    Turbo.visit(newUrl)
  }

  applyCustomFilter() {
    const urlParams = new URLSearchParams(window.location.search)

    // Remove preset parameter
    urlParams.delete('date_preset')

    // Set custom date parameters
    urlParams.set('start_date', this.startDate)
    urlParams.set('end_date', this.endDate)

    // Navigate with Turbo
    const newUrl = `${window.location.pathname}?${urlParams.toString()}`
    Turbo.visit(newUrl)
  }

  updateButtonText(text) {
    if (this.hasButtonTextTarget) {
      this.buttonTextTarget.textContent = text
    }
  }

  updateButtonTextForPreset(preset) {
    const presetNames = {
      'last_7_days': 'Last 7 Days',
      'last_30_days': 'Last 30 Days',
      'custom': 'Custom'
    }
    this.updateButtonText(presetNames[preset] || 'Last 7 Days')
  }

  formatDate(dateString) {
    const date = new Date(dateString)
    const options = { year: 'numeric', month: 'long', day: 'numeric' }
    return date.toLocaleDateString('en-US', options)
  }

  closeDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.add("hidden")
    }
  }

  toggleDropdown() {
    if (this.hasDropdownTarget) {
      this.dropdownTarget.classList.toggle("hidden")
    }
  }
}
